import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  ArrowBack as ArrowBackIcon,
  ArrowRight as ArrowRightIcon,
  ArrowLeft as ArrowLeftIcon,
  Store as StoreIcon,
} from '@mui/icons-material/';

import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import '../../../assets/css/buttons.css';

/* ---------- constants ---------- */
const CLOSE_BEFORE_MS = 25_000;
const GAMES_PER_PAGE = 10;
const BET_OPTIONS = [5, 10, 15, 20];
const CARD_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

const timeStringToTodayDate = (t) => {
  const [h, m, s = 0] = String(t ?? '0:0:0')
    .split(':')
    .map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, s || 0, 0);
  return d;
};

function ModalSellPapirKingo() {
  const { t } = useTranslation();
  const {
    updateShowModalSellPapirKingo,
    updateIsLoadingScreen,
    updateIsSellerOpen,
    updateIsPapirKingoOpen,
    hall,
    session,
    userInfo,
    parentInfo,
    updateTransactionsKioskArray,
    updateShowUserInfo,
    updateShowUserInfoSibling,
    updateShowUserInfoOK,
    updateShowUserInfoWG,
    updateShowUserInfoSiblingOK,
    closeTime,
  } = useContext(AppContext);

  const { socket } = useContext(SocketContext);

  /* ---------- state ---------- */
  const [rawGames, setRawGames] = useState([]);
  const [gamesPage, setGamesPage] = useState(1);
  const [selectedGameIds, setSelectedGameIds] = useState([]);
  const [nextGame, setNextGame] = useState(null);

  const [bet, setBet] = useState(0);
  const [cards, setCards] = useState(0);

  useEffect(() => {
    updateShowUserInfo(false);
    updateShowUserInfoSibling(false);
    updateShowUserInfoOK(false);
    updateShowUserInfoWG(false);
    updateShowUserInfoSiblingOK(false);
  }, []);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeBase = now;

  const hallRef = useRef(hall);
  useEffect(() => {
    hallRef.current = hall;
  }, [hall]);

  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const userInfoRef = useRef(userInfo);
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  const parentInfoRef = useRef(parentInfo);
  useEffect(() => {
    parentInfoRef.current = parentInfo;
  }, [parentInfo]);

  const nextGameIdRef = useRef(nextGame);
  useEffect(() => {
    nextGameIdRef.current = nextGame;
  }, [nextGame]);

  const selectedGameIdsRef = useRef(selectedGameIds);
  useEffect(() => {
    selectedGameIdsRef.current = selectedGameIds;
  }, [selectedGameIds]);

  const betRef = useRef(bet);
  useEffect(() => {
    betRef.current = bet;
  }, [bet]);

  const cardsRef = useRef(cards);
  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  /* ---------- derived data ---------- */
  const cutOffTime = (g) => {
    const base = g.dateBuyEnd
      ? timeStringToTodayDate(g.dateBuyEnd)
      : timeStringToTodayDate(g.dateStart);
    const start = timeStringToTodayDate(g.dateStart);
    const explicitOrDefault = g.dateBuyEnd
      ? base
      : new Date(start.getTime() - CLOSE_BEFORE_MS);
    return Math.min(
      explicitOrDefault.getTime(),
      start.getTime() - CLOSE_BEFORE_MS
    );
  };

  const padTime = (t) => {
    if (t == null || t === '') return t;
    const [h = '0', m = '00'] = String(t).trim().split(':');
    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
  };

  const openGames = useMemo(() => {
    const normalized = rawGames.map((g) => ({
      ...g,
      dateStart: padTime(g.dateStart),
      dateEnd: padTime(g.dateEnd),
    }));

    const closeMs = closeTime
      ? timeStringToTodayDate(closeTime).getTime()
      : null;

    const filtered = normalized.filter((g) => {
      if (cutOffTime(g) <= timeBase) return false;
      if (closeMs !== null) {
        const startMs = timeStringToTodayDate(g.dateStart).getTime();
        if (startMs >= closeMs) return false;
      }
      return true;
    });

    // drop first two games globally
    return filtered.length > 2 ? filtered.slice(2) : filtered;
  }, [rawGames, timeBase, closeTime]);

  const sortedOpenGames = useMemo(() => {
    return [...openGames].sort(
      (a, b) =>
        a.dateStart.localeCompare(b.dateStart) ||
        a.gameID - b.gameID ||
        a.name.localeCompare(b.name)
    );
  }, [openGames]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedOpenGames.length / GAMES_PER_PAGE)
  );

  const paginatedGames = useMemo(() => {
    const start = (gamesPage - 1) * GAMES_PER_PAGE;
    return sortedOpenGames.slice(start, start + GAMES_PER_PAGE);
  }, [sortedOpenGames, gamesPage]);

  useEffect(() => {
    setGamesPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const nextCutOff = useMemo(() => {
    const future = openGames.map(cutOffTime).sort((a, b) => a - b);
    return future[0] || null;
  }, [openGames]);

  const mmss = nextCutOff
    ? new Date(Math.max(0, nextCutOff - timeBase))
        .toISOString()
        .substring(14, 19)
    : '--:--';

  /* drop any selected game that is no longer open */
  useEffect(() => {
    setSelectedGameIds((prev) =>
      prev.filter((id) => openGames.some((g) => g.gameID === id))
    );
  }, [openGames]);

  useEffect(() => {
    if (openGames && openGames.length) {
      setNextGame(openGames[0].gameID);
    }
  }, [openGames]);

  /* ---------- selection summary ---------- */
  const selectedGames = openGames.filter((g) =>
    selectedGameIds.includes(g.gameID)
  );
  const selectionTotal = selectedGames.length * cards * bet;
  const canConfirm = selectedGameIds.length > 0 && cards > 0 && bet > 0;

  /* ---------- handlers ---------- */
  const handleComeBackToMainScreen = () => {
    setSelectedGameIds([]);
    setBet(0);
    setCards(0);
    updateIsPapirKingoOpen(false);
    updateIsSellerOpen(false);
    updateShowModalSellPapirKingo(false);
  };

  /* toggle a game in/out of the multi-selection */
  const toggleGame = (g) => {
    setSelectedGameIds((prev) =>
      prev.includes(g.gameID)
        ? prev.filter((id) => id !== g.gameID)
        : [...prev, g.gameID]
    );
  };

  const handleBetClick = (val) => {
    setBet(val);
  };

  const handleCardsClick = (val) => {
    setCards(val);
  };

  const handleConfirm = () => {
    const ids = selectedGameIdsRef.current;
    const qty = cardsRef.current;
    const betVal = betRef.current;
    if (ids.length === 0 || qty === 0 || betVal === 0) {
      toast.error(t('sales.betAndCardsMustBeSelected'));
      return;
    }

    const batch = openGames
      .filter((g) => ids.includes(g.gameID))
      .map((g) => ({
        name: `Ny Papir Kingo Billett - CH (${betVal} KR x${qty})`,
        amount: parseInt(betVal * qty),
        type: 'transaction',
        gameID: g.gameID,
        cutoffTime: cutOffTime(g),
        priceCard: parseInt(betVal),
        cards: qty,
        provider: 'CH',
        description: 'Ny Papir Kingo Billett',
      }));

    if (!batch.length) return;

    updateTransactionsKioskArray(batch);
    updateShowModalSellPapirKingo(false);
  };

  /* ---------- socket ---------- */
  useEffect(() => {
    socket.emit('getGamePlanKingo', '');

    const handleGetGamePlan = (data) => {
      updateIsLoadingScreen(false);
      if (!data) {
        toast.error(t('error.error'));
        return;
      }
      setRawGames(data);
    };

    socket.on('getGamePlanKingo', handleGetGamePlan);
    return () => {
      socket.off('getGamePlanKingo', handleGetGamePlan);
    };
  }, [socket]);

  const glassBg = 'rgba(255,255,255,0.06)';
  const glassBorder = '1px solid rgba(255,255,255,0.14)';
  const accent = '#FFC727';
  const betAccent = '#e3a406';

  /* ---------- render ---------- */
  return (
    <div id="react-confirm-alert ">
      <div
        className="react-confirm-alert-overlay react-confirm-alert-overlay-kiosk"
        style={{
          background: 'rgba(3,6,18,0.88)',
          padding: '24px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
        }}
      >
        <div
          className="react-confirm-alert"
          style={{
            width: 'min(1280px, 96vw)',
            height: 'min(1330px, 95vh)',
            borderRadius: '24px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 30px 90px rgba(0,0,0,0.65)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            background:
              'linear-gradient(135deg, rgba(18,26,55,0.98), rgba(10,14,28,0.98))',
          }}
        >
          <div style={{ width: '100%', padding: 0, height: '100%' }}>
            <Grid
              container
              direction="column"
              sx={{ height: '100%', flex: 1, minHeight: 0 }}
            >
              {/* HEADER */}
              <Grid
                item
                xs="auto"
                sx={{
                  height: 120,
                  flexShrink: 0,
                  borderBottom: '1px solid rgba(255,255,255,0.10)',
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    padding: '0 18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '26px',
                      fontWeight: 900,
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('sales.paper')}{' '}
                    <span
                      style={{
                        color: 'rgba(255,255,255,0.45)',
                        fontWeight: 700,
                      }}
                    >
                      · Kingo
                    </span>
                  </Typography>

                  <div
                    style={{
                      padding: '10px 12px',
                      borderRadius: '999px',
                      border: glassBorder,
                      background: glassBg,
                      color: 'white',
                      fontWeight: 900,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      fontSize: '19px',
                    }}
                  >
                    {t('sales.countdownToNextGame1')}
                    <span style={{ margin: '0 6px' }}>
                      {nextGameIdRef.current}
                    </span>
                    {t('sales.countdownToNextGame2')}
                    <span style={{ marginLeft: 6 }}>{mmss}</span>
                  </div>
                </div>
              </Grid>

              {/* CONTENT */}
              <Grid
                item
                sx={{ flex: 1, minHeight: 0, padding: '14px 18px 18px 18px' }}
              >
                <div
                  style={{
                    height: '100%',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* INNSATS selector */}
                  <Box sx={{ mb: 1.25 }}>
                    <Typography
                      sx={{
                        color: accent,
                        fontWeight: 900,
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.75,
                      }}
                    >
                      {t('sales.bet')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      {BET_OPTIONS.map((n) => {
                        const active = bet === n;
                        return (
                          <Button
                            key={n}
                            onClick={() => handleBetClick(n)}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              height: 64,
                              borderRadius: '12px',
                              fontWeight: 900,
                              fontSize: '22px',
                              textTransform: 'none',
                              color: active ? '#000' : '#fff',
                              bgcolor: active
                                ? betAccent
                                : 'rgba(255,255,255,0.08)',
                              border: active
                                ? `1px solid ${betAccent}`
                                : '1px solid rgba(255,255,255,0.14)',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: active
                                  ? '#f0b41f'
                                  : 'rgba(255,255,255,0.16)',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            {n}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* BONGER selector */}
                  <Box sx={{ mb: 1.25 }}>
                    <Typography
                      sx={{
                        color: accent,
                        fontWeight: 900,
                        fontSize: '14px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 0.75,
                      }}
                    >
                      {t('sales.cards')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      {CARD_OPTIONS.map((n) => {
                        const active = cards === n;
                        return (
                          <Button
                            key={n}
                            onClick={() => handleCardsClick(n)}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              height: 64,
                              borderRadius: '12px',
                              fontWeight: 900,
                              fontSize: '22px',
                              textTransform: 'none',
                              color: active ? '#000' : '#fff',
                              bgcolor: active
                                ? accent
                                : 'rgba(255,255,255,0.08)',
                              border: active
                                ? `1px solid ${accent}`
                                : '1px solid rgba(255,255,255,0.14)',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: active
                                  ? '#FFD54F'
                                  : 'rgba(255,255,255,0.16)',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            x{n}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Games list (multi-select) — paginated, no scroll needed:
                      the block sits vertically centered ('safe center' falls back
                      to top-aligned if a page would overflow). */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      borderRadius: '5px',
                      border: glassBorder,
                      background: glassBg,
                      padding: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'safe center',
                    }}
                  >
                    <Grid
                      container
                      spacing={1.1}
                      sx={{
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 4,
                          bottom: 4,
                          left: '50%',
                          width: '1px',
                          backgroundColor: 'rgba(255,255,255,0.14)',
                          transform: 'translateX(-50%)',
                          display: { xs: 'none', sm: 'block' },
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      {paginatedGames.map((g) => {
                        const selected = selectedGameIds.includes(g.gameID);
                        const formattedName = g.name
                          ? g.name.charAt(0).toUpperCase() +
                            g.name.slice(1).toLowerCase()
                          : '';
                        const hasPrice =
                          g.priceCard !== null &&
                          g.priceCard !== undefined &&
                          g.priceCard !== '' &&
                          Number(g.priceCard) > 0;
                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            key={`${g.gameID}-${g.dateStart}-${g.name}`}
                          >
                            <Button
                              onClick={() => toggleGame(g)}
                              variant={selected ? 'contained' : 'outlined'}
                              sx={{
                                width: '100%',
                                minHeight: 94,
                                py: 1.8,
                                borderColor: 'rgba(255,255,255,0.22)',
                                color: selected ? 'black' : '#fff',
                                bgcolor: selected
                                  ? 'primary.main'
                                  : 'transparent',
                                textAlign: 'left',
                                px: 1.5,
                                display: 'flex',
                                alignItems: 'stretch',
                                justifyContent: 'space-between',
                                gap: 1.2,
                                borderRadius: '10px',
                                '&:hover': {
                                  borderColor: '#fff',
                                  bgcolor: selected
                                    ? 'primary.dark'
                                    : 'rgba(255,255,255,0.08)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'flex-start',
                                  minWidth: 0,
                                  flex: 1,
                                  pl: 1.2,
                                  pr: 0.5,
                                  gap: 0.4,
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 900,
                                    fontSize: '23px',
                                    letterSpacing: '0.02em',
                                  }}
                                >
                                  {t('kiosk.gameLabel')} {g.gameID}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    opacity: 0.9,
                                    fontWeight: 700,
                                    fontSize: '18px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {formattedName}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    opacity: 0.92,
                                    fontWeight: 800,
                                    fontSize: '11px',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {t('kiosk.priceCardLabel')}:{' '}
                                  {hasPrice ? g.priceCard : '-'}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  minWidth: 92,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderLeft:
                                    '1px solid rgba(255,255,255,0.18)',
                                  pl: 1.2,
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    opacity: 0.98,
                                    fontWeight: 900,
                                    fontSize: '24px',
                                    lineHeight: 1,
                                    letterSpacing: '0.01em',
                                  }}
                                >
                                  {g.dateStart}
                                </Typography>
                              </Box>
                            </Button>
                          </Grid>
                        );
                      })}

                      {!paginatedGames.length && (
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              color: 'rgba(255,255,255,0.72)',
                              textAlign: 'center',
                              py: 2,
                            }}
                          >
                            {t('kiosk.noGamesAvailable')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Pagination */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '16px',
                      color: 'white',
                      pt: '12px',
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => setGamesPage((p) => Math.max(1, p - 1))}
                      disabled={gamesPage === 1}
                      sx={{
                        minWidth: '64px',
                        height: '64px',
                        borderRadius: '14px',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        color: 'rgba(255,255,255,0.75)',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.14)',
                          boxShadow: 'none',
                        },
                        '&:disabled': { opacity: 0.3 },
                      }}
                    >
                      <ArrowLeftIcon sx={{ fontSize: '40px' }} />
                    </Button>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.85)',
                        fontWeight: 800,
                        fontSize: '22px',
                        minWidth: '80px',
                        textAlign: 'center',
                      }}
                    >
                      {t('kiosk.pageIndicator', {
                        current: gamesPage,
                        total: totalPages,
                      })}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() =>
                        setGamesPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={gamesPage === totalPages}
                      sx={{
                        minWidth: '64px',
                        height: '64px',
                        borderRadius: '14px',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.14)',
                        color: 'rgba(255,255,255,0.75)',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.14)',
                          boxShadow: 'none',
                        },
                        '&:disabled': { opacity: 0.3 },
                      }}
                    >
                      <ArrowRightIcon sx={{ fontSize: '40px' }} />
                    </Button>
                  </Box>

                  {/* Selection summary */}
                  <Box
                    sx={{
                      mt: 1.5,
                      width: '100%',
                      borderRadius: '12px',
                      border: glassBorder,
                      background: glassBg,
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    {canConfirm ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          width: '100%',
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flex: '1 1 auto',
                            minWidth: 0,
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            touchAction: 'pan-x',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            '-ms-overflow-style': 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                            maskImage:
                              'linear-gradient(to right, #000 calc(100% - 28px), transparent 100%)',
                            WebkitMaskImage:
                              'linear-gradient(to right, #000 calc(100% - 28px), transparent 100%)',
                            fontSize: '20px',
                            fontWeight: 700,
                            color: '#fff',
                          }}
                        >
                          {[...selectedGames]
                            .sort((a, b) => a.gameID - b.gameID)
                            .map((g, i) => {
                              const lineTotal = cards * bet;
                              return (
                                <Box
                                  component="span"
                                  key={g.gameID}
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  {i > 0 && (
                                    <Box
                                      component="span"
                                      sx={{ color: 'rgba(255,255,255,0.3)' }}
                                    >
                                      ·
                                    </Box>
                                  )}
                                  <Box component="span" sx={{ fontWeight: 900 }}>
                                    {t('kiosk.gameLabel')} {g.gameID}
                                  </Box>
                                  <Box
                                    component="span"
                                    sx={{
                                      opacity: 0.7,
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}
                                  >
                                    {cards}×{bet} = {lineTotal} kr
                                  </Box>
                                </Box>
                              );
                            })}
                        </Box>

                        <Box
                          component="span"
                          sx={{
                            flex: '0 0 auto',
                            ml: 'auto',
                            pl: 2,
                            fontWeight: 900,
                            fontSize: '24px',
                            color: accent,
                          }}
                        >
                          {selectionTotal} kr
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '20px',
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.55)',
                        }}
                      >
                        {t('sales.selectGameAndCards')}
                      </Typography>
                    )}
                  </Box>

                  {/* Footer actions */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 3,
                      pt: '14px',
                    }}
                  >
                    <Button
                      onClick={() => handleComeBackToMainScreen()}
                      startIcon={<ArrowBackIcon sx={{ fontSize: '1.2em' }} />}
                      sx={{
                        height: 100,
                        width: 220,
                        borderRadius: '16px',
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 800,
                        fontSize: 22,
                        textTransform: 'uppercase',
                        border: glassBorder,
                        boxShadow: 'none',
                        transition:
                          'transform 0.15s ease, background 0.15s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.14)',
                          transform: 'translateY(-2px)',
                          color: 'white',
                        },
                        '&:active': { transform: 'translateY(0)' },
                      }}
                    >
                      {t('sales.cancel')}
                    </Button>

                    <Button
                      onClick={() => handleConfirm()}
                      startIcon={<StoreIcon sx={{ fontSize: '1.2em' }} />}
                      sx={{
                        height: 100,
                        width: 220,
                        borderRadius: '16px',
                        bgcolor: accent,
                        color: '#000',
                        fontWeight: 800,
                        fontSize: 22,
                        textTransform: 'uppercase',
                        opacity: canConfirm ? 1 : 0.25,
                        boxShadow: `0 12px 32px ${accent}66`,
                        transition:
                          'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          bgcolor: '#FFD54F',
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 40px ${accent}110`,
                        },
                        '&:active': { transform: 'translateY(0)' },
                      }}
                    >
                      {t('sales.confirm')}
                    </Button>
                  </Box>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalSellPapirKingo;
